<template>
  <div v-if="!promo" class="promos-wrap"><p>{{ $t('promos.verdict.notFound') }}</p></div>

  <!-- The engine could not measure it at all: a zone with no daily history, most
       often one drawn after the promo ran. Say which promo and why, rather than a
       page of blanks.

       `!m.phase` is the second half of that, and it is the important one: a
       measurement that arrives EMPTY is not an error, so it used to fall through to
       the full page, which then invented a blank verdict pill, "Per day, in .", an
       empty before/after table and a "Still running" that no payload had said. Every
       figure on this page comes from the measurement; without one there is nothing
       to draw, and drawing it anyway is how a contract bug reads as a design bug. -->
  <div v-else-if="m.error || !m.phase" class="promos-wrap">
    <button type="button" class="back"
            @click="$router.push(demo ? '/storalytic_demo_store/promotions' : '/promotions')">
      <i class="bi bi-arrow-left"></i> {{ $t('promos.verdict.allPromotions') }}
    </button>
    <Section :title="promo.Data.name" margin-top="16px">
      <p v-if="noHistory" class="lead">
        {{ $t('promos.verdict.noHistoryBefore') }} <b>{{ scopeName }}</b>
        {{ $t('promos.verdict.noHistoryAfter') }}
      </p>
      <p v-else class="lead">
        {{ $t('promos.verdict.cannotMeasure') }}
        <span v-if="m.error" class="why-err">{{ m.error }}</span>
      </p>
    </Section>
  </div>
  <div v-else class="promos-wrap">
    <!-- Back to the tab this promo lives in, not to whatever the default is. You
         clicked it in Measured; you expect to land in Measured. -->
    <button type="button" class="back"
            @click="$router.push({ path: demo ? '/storalytic_demo_store/promotions'
                                              : '/promotions',
                                   query: { stage: backStage } })">
      <i class="bi bi-arrow-left"></i> {{ $t('promos.verdict.allPromotions') }}
    </button>

    <Section :title="promo.Data.name" margin-top="16px">

      <!-- Built from the parts that EXIST. A store-wide promo has no zone and often
           no product scope, which left the line reading "· · 3 Oct – 3 Oct" — two
           separators around nothing. -->
      <p class="sub">
        {{ subParts.join(' · ') }}
        <span v-if="phaseLabel(m.phase)" class="verdict-chip"
              :class="m.phase === 'complete' ? 'v-flat' : 'v-early'">
          {{ phaseLabel(m.phase) }}
        </span>
      </p>
      <!-- §9 item 5: a finished promo keeps the result it had that day. -->
      <p v-if="m.frozen && m.measuredUntil" class="measured-until">
        <i class="bi bi-lock"></i> {{ $t('promos.verdict.measuredUntil', { day: sayDay(m.measuredUntil) }) }}
      </p>

      <!-- Only while it is still running — the one part of the page that can
           change an outcome instead of explaining one. -->
      <div v-if="promo.live" class="alarms">
        <p class="alarms-head">{{ $t('promos.verdict.alarmsHead') }}</p>
        <div v-for="a in promo.live.alarms" :key="a.kind" class="alarm">
          <i class="bi bi-exclamation-octagon-fill"></i>
          <div><b>{{ a.title }}</b><p>{{ a.text }}</p></div>
        </div>
      </div>

      <!-- ===================== 1. MONEY FIRST =====================
           Margin before behaviour, because margin is the decision. And
           incrementality beside it, because the two together answer
           "do I run this again?" — either alone can mislead. -->
      <!-- The arc. "Gross margin" is said in the section head and on the caption,
           not left to a footnote underneath — the reader needs to know which money
           this is before they read the number, not after. -->
      <!-- ONE result card, read top to bottom as a story:
             what you did -> what happened -> what it made -> what to do next.
           The money used to come first, but you arrive here from a card that already
           showed it, so leading with the number again told you nothing new. And the
           outcome is a sentence, not two pills: an uppercase chip dropped into a line
           of prose cannot share a baseline with the words around it, which is why
           that row never aligned. -->
      <div class="result">
        <!-- Two prose lines, each given a role by an eyebrow and set as a deck:
             lighter and larger than body copy, not bold black. They were previously
             undifferentiated text sitting above a figures band, which is why they
             read as an afterthought rather than the lead. -->
        <div class="res-story">
          <div class="res-row">
            <div class="res-eyebrow">{{ $t('promos.verdict.whatYouDid') }}</div>
            <p class="res-text">{{ changeLine(m, promo.Data) }}</p>
          </div>
          <div class="res-row">
            <div class="res-eyebrow">{{ $t('promos.verdict.whatHappened') }}</div>
            <p v-if="outcome" class="res-text res-outcome">
              <template v-for="(seg, i) in outcome" :key="i"
                ><b v-if="seg.tone" :class="'t-' + seg.tone">{{ seg.t }}</b
                ><template v-else>{{ seg.t }}</template
              ></template>
            </p>
            <!-- Keyed on the phase, not on "the outcome sentence came back empty":
                 a settled promo whose figures are too thin to call was being
                 announced as still running, which is a claim about the calendar
                 made from a gap in the data. -->
            <p v-else-if="m.phase === 'planned'" class="res-text">
              {{ $t('promos.verdict.notStarted') }}
            </p>
            <p v-else-if="m.phase === 'running'" class="res-text">
              {{ $t('promoVocab.outcome.stillRunning') }}
            </p>
            <p v-else class="res-text">
              {{ $t('promos.verdict.notEnough') }}
            </p>
          </div>
        </div>

        <!-- No figures, no money band. A promo four days in has nothing to show,
             and printing a euro we invented is the one thing this page must not do. -->
        <div v-if="!hasMoney" class="arc arc-empty">
          <div>
            <b>{{ $t('promos.verdict.noSalesYet') }}</b>
            <!-- keyed off whether we can actually take them, not off the phase: a
                 button that opens an empty table is worse than no button -->
            <template v-if="salesAsk.length">
              <p>
                {{ $t('promos.verdict.addThem') }}
              </p>
              <button type="button" class="btn-primary" @click="showSales = !showSales">
                {{ showSales ? $t('promos.common.close') : $t('promos.verdict.addFigures') }}
              </button>
            </template>
            <template v-else>
              <p>{{ salesNote }}</p>
              <p class="arc-empty-still">
                {{ $t('promos.verdict.untilThen') }}
              </p>
            </template>
          </div>
        </div>

        <div v-else class="arc">
          <div class="arc-step">
            <div class="arc-eur" :class="m.margin.duringEur < 0 ? 'neg' : 'pos'">
              {{ eur(m.margin.duringEur) }}
            </div>
            <div class="arc-cap">{{ $t('promos.verdict.whileItRan') }}</div>
            <div class="arc-sub">{{ dayS(m.windows.window.days) }}</div>
          </div>

          <div class="arc-op">+</div>

          <div class="arc-step">
            <div class="arc-eur" :class="afterEurClass">
              {{ m.margin.afterKnown ? eur(m.margin.afterEur) : $t('promos.list.notInYet') }}
            </div>
            <div class="arc-cap">{{ $t('promos.verdict.weeksAfter', { weeks: afterWeeks }) }}</div>
            <div class="arc-sub">
              {{ m.margin.afterKnown ? $t('promos.verdict.atFullPrice') : $t('promos.verdict.lookAgain') }}
            </div>
          </div>

          <div class="arc-op">=</div>

          <div class="arc-step arc-net">
            <div class="arc-eur" :class="m.margin.eur < 0 ? 'neg' : 'pos'">{{ eur(m.margin.eur) }}</div>
            <div class="arc-cap">
              <!-- "in total" is a promise that nothing else is coming. While the
                   weeks after are still running, something is. -->
              {{ m.margin.afterKnown ? $t('promos.verdict.grossInTotal') : $t('promos.verdict.grossSoFar') }}
              <Tooltip :text="GROSS_MARGIN_HELP" placement="top" align="end">
                <span class="helpq" :aria-label="$t('promos.verdict.whatIsGross')">?</span>
              </Tooltip>
            </div>
            <div class="arc-sub">
              {{ $t('promos.verdict.afterSetup', { eur: euro(m.margin.fixedCostEur) }) }}
            </div>
            <!-- Where the euro is, not in a footnote: a figure worked out from an
                 average margin must never pass for one read off a cost report. -->
            <div v-if="marginPctUsed" class="arc-sub arc-modelled">
              {{ $t('promos.verdict.workedFromPct', { pct: rawNum(marginPctUsed) }) }}
            </div>
          </div>
        </div>

        <!-- THE AUDIT TRAIL, as one table behind one toggle.
             It used to be three stacked blocks — a figures table, then a ledger
             restating those same gross figures, then a net line restating the band
             above — all expanded by default, so ~25 rows of arithmetic sat between
             the headline and the behaviour story. One table now carries the inputs
             AND the subtraction: read a row across and `Gross − Normal = Difference`
             is right there. Collapsed, because it answers "prove it", which is a
             question asked second. -->
        <div v-if="figures && !showSales" class="audit">
          <button type="button" class="audit-t" @click="showWork = !showWork"
                  :aria-expanded="showWork">
            <i class="bi" :class="showWork ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            {{ showWork ? $t('promos.verdict.hideWorking') : $t('promos.verdict.howWeWorked', { eur: eur(m.margin.eur) }) }}
          </button>

          <div v-if="showWork" class="audit-body">
            <!-- THE ONE DERIVED NUMBER, derived on screen.
                 Every "Normal for those days" figure in the table is this rate times
                 a number of days, and until it was shown as a division nobody could
                 see where it came from — it was a grey sentence under the table
                 saying the rate exists. Arithmetic a ten-year-old can follow, in the
                 same visual language as the money band above, and placed BEFORE the
                 column it explains rather than after it. -->
            <div class="rate">
              <div class="rate-step">
                <div class="rate-num">{{ euro(figures.beforeGross) }}</div>
                <div class="rate-cap">
                  {{ storeWide ? $t('promos.verdict.yourGrossStore') : $t('promos.verdict.yourGrossZone') }}
                </div>
                <div class="rate-sub">{{ $t('promos.verdict.inTheDaysBefore', { days: dayS(figures.beforeDays) }) }}</div>
              </div>
              <div class="rate-op">÷</div>
              <div class="rate-step">
                <div class="rate-num">{{ figures.beforeDays }}</div>
                <div class="rate-cap">{{ $t('promos.common.dayWord', figures.beforeDays) }}</div>
                <div class="rate-sub">&nbsp;</div>
              </div>
              <div class="rate-op">=</div>
              <div class="rate-step rate-answer">
                <div class="rate-num">{{ euroFixed(figures.perDay) }}</div>
                <div class="rate-cap">{{ $t('promos.verdict.grossPerDay') }}</div>
                <div class="rate-sub">{{ $t('promos.verdict.noPromoActive') }}</div>
              </div>
            </div>
            <p class="rate-then">
              {{ $t('promos.verdict.withoutPromo1') }}
              <b>{{ dayS(figures.windowDays) }}</b> {{ $t('promos.verdict.withoutPromo2') }}
              {{ figures.windowDays }} × {{ euroFixed(figures.perDay) }} =
              <b>{{ euro(figures.windowNormal) }}</b> {{ $t('promos.verdict.withoutPromo3') }}
              <i>{{ normalForDaysPlain }}</i> {{ $t('promos.verdict.withoutPromo4') }}
            </p>

            <table class="fig">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ storeWide ? $t('promos.common.transactions') : $t('promos.common.unitsSold') }}</th>
                  <th>{{ $t('promos.common.revenue') }}</th>
                  <!-- Store-wide there is no cost column to show: the gross comes
                       from one average margin, and the heading says so rather than
                       printing a column of dashes. -->
                  <th v-if="!storeWide">{{ $t('promos.verdict.costOfGoods') }}</th>
                  <th>
                    {{ $t('promos.common.grossMarginEur') }}<small v-if="marginPctUsed">{{ $t('promos.verdict.grossAt', { pct: rawNum(marginPctUsed) }) }}</small>
                  </th>
                  <th>{{ $t('promos.verdict.normalForDays') }}</th>
                  <th class="col-diff">{{ $t('promos.verdict.difference') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in figures.rows" :key="row.key">
                  <td>
                    <b>{{ row.label }}</b>
                    <span class="fig-when">{{ row.when }}</span>
                  </td>
                  <td :data-label="storeWide ? $t('promos.common.transactions') : $t('promos.common.unitsSold')"
                      class="num">{{ num(row.units) }}</td>
                  <td :data-label="$t('promos.common.revenue')" class="num">{{ num(row.revenue) }}</td>
                  <td v-if="!storeWide" :data-label="$t('promos.verdict.costOfGoods')"
                      class="num">{{ num(row.cost) }}</td>
                  <td :data-label="$t('promos.common.grossMarginEur')" class="num strong">{{ num(row.gross) }}</td>
                  <td :data-label="$t('promos.verdict.normalForDays')" class="num">{{ num(row.normal) }}</td>
                  <td :data-label="$t('promos.verdict.difference')" class="col-diff">
                    <span v-if="row.diff !== null" :class="row.diff < 0 ? 'neg' : 'pos'">
                      {{ led(row.diff) }}
                    </span>
                    <span v-else class="gross-na">—</span>
                    <span v-if="row.diffNote" class="fig-note neg">{{ row.diffNote }}</span>
                  </td>
                </tr>
                <tr class="fig-total">
                  <td :colspan="storeWide ? 5 : 6">{{ $t('promos.verdict.grossTotal') }}</td>
                  <td class="col-diff" :data-label="$t('promos.verdict.grossTotal')">
                    {{ eur(m.margin.eur) }}
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-if="anyCostMissing" class="sales-ask-warn">
              <i class="bi bi-exclamation-triangle"></i>
              {{ $t('promos.verdict.noCostGiven') }}
            </p>
            <p class="sales-edit">
              <button v-if="canEdit" type="button" @click="showSales = true">{{ $t('promos.verdict.changeFigures') }}</button>
            </p>
          </div>
        </div>

        <p v-else-if="hasMoney && !figures" class="sales-edit">
          <button type="button" @click="showSales = true">{{ $t('promos.verdict.addFigures') }}</button>
        </p>

        <!-- Three numbers, with the exact dates named. Not a daily feed: almost
             nobody types sales every day, they pull a report once the promo is over
             and give three totals — which is all the money answer needs. -->
        <div v-if="showSales" class="sales-ask">
          <p class="sales-ask-why">
            <template v-if="hasMoney">
              {{ $t('promos.verdict.figuresBuiltOn') }}
            </template>
            <template v-else>
              {{ $t('promos.common.readPos') }}
            </template>
            <!-- "Optional" was the wrong word for the cost column: leaving it out does
                 not drop the margin, it changes where the margin comes from. Said
                 here rather than as one word under a heading. -->
            <template v-if="storeWide">
              {{ $t('promos.verdict.storeFiguresNote') }}
            </template>
            <template v-else>
              {{ $t('promos.verdict.costNote1') }} <b>{{ $t('promos.verdict.costNoteMeasured') }}</b>{{ $t('promos.verdict.costNote2') }}
            </template>
          </p>
          <table class="sales-ask-t">
            <thead>
              <tr>
                <th></th>
                <!-- "Units sold" is a product-zone question. Store-wide it would mean
                     every item in the shop; what a POS report gives for a day is the
                     number of TRANSACTIONS, which is a different figure and an
                     easy one — and the word the Guidance tab's sales form already
                     uses, so the two do not teach different vocabularies. -->
                <th>{{ storeWide ? $t('promos.common.transactions') : $t('promos.common.unitsSold') }}</th>
                <th>{{ $t('promos.common.revenue') }}</th>
                <!-- Cost is asked per period only where there is something to fall
                     back on if it is missing: the item prices. Store-wide there is no
                     item list, and whole-store cost of goods for four specific
                     Saturdays is not a figure anyone has to hand — so it is asked
                     once, as an average, below the table. -->
                <th v-if="!storeWide">
                  {{ $t('promos.common.goodsCost') }}<small>{{ $t('promos.common.goodsCostSmall') }}</small>
                </th>
                <th class="col-gross">{{ $t('promos.common.grossMarginEur') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in salesAsk" :key="row.key">
                <td>
                  <b>{{ row.label }}</b>
                  <span class="sales-when">{{ row.when }}</span>
                </td>
                <td :data-label="$t('promos.common.unitsSold')">
                  <input type="number" min="0" :disabled="row.pending"
                         v-model.number="saleDraft[row.key].units" /></td>
                <td :data-label="$t('promos.common.revenue')">
                  <input type="number" min="0" :disabled="row.pending"
                         v-model.number="saleDraft[row.key].revenue" /></td>
                <td v-if="!storeWide" :data-label="$t('promos.common.goodsCost')">
                  <input type="number" min="0" :disabled="row.pending"
                         v-model.number="saleDraft[row.key].cost" /></td>
                <!-- the subtraction, not a field: this is where the euro above
                     comes from, so it is shown rather than implied -->
                <td class="col-gross" :data-label="$t('promos.common.grossMarginEur')">
                  <span v-if="grossOf(row) !== null" class="gross-val">
                    {{ num(grossOf(row)) }}
                  </span>
                  <span v-else class="gross-na">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Set-up cost lives HERE and not only on the log form, because it is
               the third thing the euro is made of and it is routinely unknown when
               the promo is logged: the print bill, the stand, the gifts you bought
               all arrive after the fact. It is a flat sum, not a per-period one —
               you spend it once, whatever the turnout. -->
          <!-- Asked once, not per period: it is the store's average, and asking for
               it three times would imply we could tell the difference. -->
          <label v-if="storeWide" class="setup-cost setup-margin">
            <span class="setup-label">{{ $t('promos.common.averageMargin') }}</span>
            <input type="number" min="1" max="99" step="0.5" v-model.number="marginDraft" />
            <small class="setup-hint">
              {{ $t('promos.verdict.marginHint1') }} <b>{{ $t('promos.verdict.marginHintBold') }}</b>{{ $t('promos.verdict.marginHint2') }}
            </small>
          </label>

          <label class="setup-cost setup-fixed">
            <span class="setup-label">{{ $t('promos.verdict.setupLabel') }}</span>
            <input type="number" min="0" step="1" v-model.number="setupDraft" />
            <small class="setup-hint">
              {{ $t('promos.verdict.setupHint1') }}
              <b>{{ $t('promos.verdict.setupHintBold') }}</b>{{ $t('promos.verdict.setupHint2') }}
            </small>
          </label>

          <!-- Cancel sits BESIDE save, where a person looks for it. It used to be a
               "Close" text link above the table, which is not where anyone looks for
               the way out of a form. -->
          <div class="sales-actions">
            <button type="button" class="btn-primary" :disabled="!saleDirty"
                    @click="onSave">{{ $t('promos.verdict.saveFigures') }}</button>
            <button type="button" class="btn-ghost" @click="cancelSales">{{ $t('promos.common.cancel') }}</button>
            <span v-if="saleDirty" class="sales-dirty">{{ $t('promos.verdict.unsaved') }}</span>
          </div>
          <p v-if="savedNote" class="sales-saved">
            <i class="bi bi-info-circle"></i>
            <template v-if="demo">
              {{ $t('promos.verdict.demoSaved') }}
            </template>
            <template v-else>
              {{ $t('promos.verdict.saved') }}
            </template>
          </p>
        </div>

        <!-- The break-even. It lived on the overview card and nowhere else, so the
             redesign would have dropped the single most quotable line in the feature:
             how many more you needed to sell just to stand still. -->
        <p v-if="breakEven" class="res-be">
          <i class="bi bi-calculator"></i>{{ breakEven }}
        </p>

        <p v-if="after" class="res-do" :class="afterCls">
          <i class="bi bi-arrow-right-short"></i>{{ after.soWhat }}
        </p>
      </div>
    </Section>

    <!-- ===================== BEFORE AND AFTER =====================
         A table, because a table is the clearest way to put two columns of figures
         side by side — a bar funnel looked designed but took longer to read. What it
         was missing was the CHANGE column: without it every row made the reader do
         the arithmetic. The title says what the columns are and no longer repeats
         "what happened" from the card above. -->
    <Section :title="$t('promos.verdict.beforeAfter')" margin-top="34px">
      <p class="lead">{{ storeWide ? $t('promos.verdict.perDayStore') : $t('promos.verdict.perDayZone', { zone: m.zone }) }}</p>

      <table class="counted">
        <thead>
          <tr>
            <th></th>
            <th>{{ $t('promos.verdict.normally') }}</th>
            <th class="col-after">{{ $t('promoVocab.rows.during') }}</th>
            <th v-if="hasAfter">{{ theWeeksAfter }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="st in steps" :key="st.key">
            <td>
              {{ st.label }}<span v-if="st.term" class="term">({{ termLabel(st.term) }})</span>
              <span v-if="st.sub" class="src">{{ st.sub }}</span>
            </td>
            <td :data-label="$t('promos.verdict.normally')">{{ fig(st.before) }}{{ st.unit }}</td>
            <td class="col-after" :data-label="$t('promoVocab.rows.during')">
              <b>{{ fig(st.during) }}{{ st.unit }}</b>
              <span class="delta" :class="deltaTone(st.dDuring, st)">
                {{ deltaText(st, st.dDuring) }}<template v-if="isNoise(st, st.dDuring)">*</template>
              </span>
            </td>
            <td v-if="hasAfter" :data-label="theWeeksAfter">
              {{ fig(st.after) }}{{ st.unit }}
              <span class="delta" :class="deltaTone(st.dAfter, st)">
                {{ deltaText(st, st.dAfter) }}<template v-if="isNoise(st, st.dAfter)">*</template>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="note">
        {{ $t('promos.verdict.eachChange') }}
        <!-- Said, not left as a missing column. The during read is finished the day
             the promo ends; the weeks after are an addition to it, and we do not
             print a partial one as if it were the lot. -->
        <template v-if="!hasAfter && afterDue">
          {{ $t('promos.verdict.afterRunning1', { weeks: afterWeeks }) }}
          <b>{{ afterDue }}</b>{{ $t('promos.verdict.afterRunning2') + ' ' }}
        </template>
        <template v-if="anyNoise">
          {{ $t('promos.verdict.noise1') }} <b>*</b>
          {{ $t(storeWide ? 'promos.verdict.noise2Store' : 'promos.verdict.noise2Zone',
                { bar: Math.round(noiseBar) }) }}
        </template>
      </p>

      <!-- The conclusion drawn from the table, tied to it visually rather than left
           as another paragraph of black text. -->
      <div v-if="attention" class="attn" :class="attention.caveat ? 'attn-soft' : ''">
        <i class="bi bi-eye"></i>
        <div>
          <b>{{ attention.headline }}</b>
          <p v-if="attention.action">{{ attention.action }}</p>
          <p v-if="attention.caveat" class="attn-caveat">{{ attention.caveat }}</p>
        </div>
      </div>
    </Section>

    <!-- ===================== 4. THE CHART ===================== -->
    <Section :title="$t('promos.verdict.dayByDay')" margin-top="34px">
      <p class="lead">
        {{ chartLead }} {{ $t('promos.verdict.chartTail') }}
      </p>
      <v-chart class="chart" :option="chartOption" autoresize />
    </Section>

    <!-- ===================== 5. WHAT COULD BE OFF =====================
         Every line here names the thing we can actually point at: a logged record,
         a camera count, a correlation the weather pipeline computed. Nothing is
         asserted that a manager could not check. -->
    <Section :title="$t('promos.verdict.throwingOff')" margin-top="34px">
      <div class="caveats">
        <!-- The most important one: the whole store moved over those days too, and
             only the difference belongs to the promo. -->
        <!-- What the STORE did over the same days, from its own door count. Stated,
             never subtracted: nothing above has been adjusted for it, and what to make
             of a busy fortnight is the reader's call. This replaces a cross-zone
             correction that assumed product zones move together. -->
        <div v-if="store && !storeWide" class="cv">
          <i class="bi bi-shop"></i>
          <div>
            <template v-if="store.beyondNormal">
              {{ $t('promos.verdict.storeBeyond', { pct: store.pct, word: store.word,
                    during: fig(store.duringPerDay), before: fig(store.beforePerDay) }) }}
            </template>
            <template v-else>
              {{ $t('promos.verdict.storeUsual', {
                    during: fig(store.duringPerDay), before: fig(store.beforePerDay) }) }}
            </template>
          </div>
        </div>
        <div v-if="m.coverage && m.coverage.footfallIsHardToRead" class="cv">
          <i class="bi bi-camera-video"></i>
          <div>
            {{ $t('promos.verdict.coverageCaveat', { label: m.coverage.label }) }}
            <b>{{ $t('promos.verdict.coverageBold') }}</b>
          </div>
        </div>
        <div v-if="comparison" class="cv">
          <i class="bi bi-rulers"></i>
          <div>
            {{ comparison }}
          </div>
        </div>
        <div v-if="m.excludedDays" class="cv">
          <i class="bi bi-calendar-x"></i>
          <div>
            {{ $t('promos.verdict.excludedDays', { n: m.excludedDays }) }}
          </div>
        </div>
        <div v-for="c in m.confoundingActions" :key="c.actionId" class="cv">
          <i class="bi bi-signpost-split"></i>
          <div>
            {{ $t('promos.verdict.confounding', { what: c.what }) }}
          </div>
        </div>
        <div v-if="m.weatherCaveat" class="cv">
          <i class="bi bi-cloud-sun"></i>
          <div>
            <template v-if="storeWide">
              {{ $t('promos.verdict.weatherStore') }}
            </template>
            <template v-else>
              {{ $t('promos.verdict.weatherZone') }}
            </template>
          </div>
        </div>
        <!-- Named where the other limits are named, so it reads as a property of the
             zone rather than as something missing from the result. -->
        <div v-if="countsOnly && !storeWide" class="cv">
          <i class="bi bi-slash-lg"></i>
          <div>
            {{ $t('promos.verdict.countingLine', { zone: m.zone }) }}
          </div>
        </div>

        <div class="cv">
          <i class="bi bi-question-circle"></i>
          <div>
            <template v-if="storeWide">
              {{ $t('promos.verdict.limitsStore') }}
            </template>
            <template v-else-if="hasMoney">
              {{ $t('promos.verdict.limitsMoney') }}
            </template>
            <template v-else>
              {{ $t('promos.verdict.limitsNoMoney') }}
            </template>
          </div>
        </div>
      </div>
    </Section>

    <!-- ===================== 6. TAKEAWAY ===================== -->
    <!-- The figures already say whether it worked. What they cannot say is why, or
         what you would do differently — so the question is the decision, not the
         verdict. And it is only worth asking because the log form reads it back when
         you next promote in this zone. -->
    <Section :title="$t('promos.verdict.runAgain')" margin-top="34px">
      <p class="lead">
        {{ $t('promos.verdict.runAgainLead') }}
        <b v-if="storeWide">{{ $t('promos.verdict.nextStore') }}</b>
        <b v-else>{{ $t('promos.verdict.nextZone', { zone: m.zone }) }}</b>
      </p>
      <div class="attrs">
        <label v-for="(o, k) in REPEAT" :key="k" class="attr" :class="{ sel: repeat === k }">
          <input type="radio" :value="k" v-model="repeat" /> <span>{{ o.label }}</span>
        </label>
      </div>
      <label class="why">
        <span>{{ $t('promos.verdict.worthRemembering') }}</span>
        <input type="text" v-model="repeatNote"
               :placeholder="$t('promos.verdict.rememberPlaceholder')" />
      </label>

      <!-- Without this the radios were a dead end: you answered, nothing happened,
           and the "Would you repeat it?" pill on the overview went on asking. The
           demo keeps the answer for the session so the loop can actually be shown —
           answer here, go back, the pill has become your answer. -->
      <div class="form-actions">
        <button type="button" class="btn-primary" :disabled="!repeatDirty"
                @click="saveRepeat">{{ $t('promos.verdict.saveAnswer') }}</button>
        <button v-if="repeatDirty" type="button" class="btn-ghost"
                @click="resetRepeat">{{ $t('promos.common.cancel') }}</button>
        <span v-if="!repeatDirty && !repeat" class="form-hint">{{ $t('promos.verdict.pickOne') }}</span>
      </div>
      <p v-if="repeatSaved" class="demo-note">
        <i class="bi bi-info-circle"></i>
        <template v-if="demo">
          {{ $t('promos.verdict.demoAnswer', { zone: m.zone }) }}
        </template>
        <template v-else>
          {{ $t('promos.verdict.answerSaved', { zone: m.zone }) }}
        </template>
      </p>
    </Section>
  </div>
</template>

<script setup>
import { sayDay } from '@/zones/zoneMarkers'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEuro, formatNumber } from '@/i18n/format'
import Section from '@/components/ui/Section.vue'
import Tooltip from '@/components/ui/Tooltip.vue'

import {
  REPEAT, after as afterOf, afterClass, attentionFrom, breakEvenLine, changeLine,
  salesRows,
  salesRowsNote,
  eur, fmtRange, outcomeSegments, phaseLabel,
} from '@/promotions/vocab'

import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent, CanvasRenderer])

/**
 * One promo's verdict page, with no idea where its promo came from. The demo store
 * hands it a fixture; the live portal hands it the API's measurement payload.
 */
const props = defineProps({
  /** The promo, with its `measurement` block. Null renders the not-found state. */
  promo: { type: Object, default: null },
  /** True on the demo store: nothing is persisted, and the page says so. */
  demo: { type: Boolean, default: false },
  /** Writes are admin-only in production. */
  canEdit: { type: Boolean, default: true },
})
const emit = defineEmits(['save-sales', 'save-outcome'])

const promo = computed(() => props.promo)
const m = computed(() => promo.value?.measurement || {})

const TONE = { good: 'v-improved', bad: 'v-declined', flat: 'v-flat' }

const { t, te, locale } = useI18n()
const isNl = () => locale.value === 'nl'

const GROSS_MARGIN_HELP = computed(() => t('promos.verdict.grossHelp'))
/** The audit column's heading, without its euro sign, as the sentence above names it. */
const normalForDaysPlain = computed(() => t('promos.verdict.normalForDays').replace(/\s*€$/, ''))
const theWeeksAfter = computed(() => {
  const s = t('promos.verdict.weeksAfter', { weeks: afterWeeks.value })
  return s.charAt(0).toUpperCase() + s.slice(1)
})

const repeat = ref(promo.value?.Data?.outcome?.repeat || null)
const repeatNote = ref(promo.value?.Data?.outcome?.note || '')
const repeatSaved = ref(false)
const repeatDirty = computed(() =>
  repeat.value !== (promo.value?.Data?.outcome?.repeat || null)
  || repeatNote.value !== (promo.value?.Data?.outcome?.note || ''))

function resetRepeat() {
  repeat.value = promo.value?.Data?.outcome?.repeat || null
  repeatNote.value = promo.value?.Data?.outcome?.note || ''
  repeatSaved.value = false
}
/**
 * Writes into the demo's own in-memory promo, so the answer survives navigation for
 * the length of the session. That is the point: the loop this section describes —
 * answer it here, see it on the list and on the next promo you log in this zone —
 * cannot be demonstrated if the answer evaporates the moment you navigate.
 */
function saveRepeat() {
  const d = promo.value?.Data
  if (!d) return
  d.outcome = { ...(d.outcome || {}), repeat: repeat.value, note: repeatNote.value }
  repeatSaved.value = true
  if (!props.demo) emit('save-outcome', { repeat: repeat.value, note: repeatNote.value })
}

// ---- chart ----
/**
 * What the chart counts, which is not the same thing in every zone. `engaged` is
 * people who stopped — a polygon figure. A counting line has none, and a store-wide
 * promo has none either, so plotting it drew a flat line at zero under a heading
 * that said people were stopping. Both fall back to the count they do have.
 */
const chartSeries = computed(() => (storeWide.value || countsOnly.value
  ? { points: m.value.daily?.visitors || [], axis: t('promos.verdict.axisVisits') }
  : { points: m.value.daily?.engaged || [], axis: t('promos.verdict.axisEngaged') }))
const chartLead = computed(() => (storeWide.value
  ? t('promos.verdict.chartStore')
  : countsOnly.value
    ? t('promos.verdict.chartLine', { zone: m.value.zone })
    : t('promos.verdict.chartShelf')))
const chartOption = computed(() => {
  const eng = chartSeries.value.points
  // `windows` is absent on a measurement we could not make; the page shows its
  // unmeasurable state instead of this, but a computed that throws takes the whole
  // render with it, so it does not depend on being unreachable.
  const base = m.value.windows?.baseline || {}
  const win = m.value.windows?.window || {}
  const baselineVals = eng.filter((p) => p.date < win.start).map((p) => p.value)
  const baseline = baselineVals.length
    ? baselineVals.reduce((a, b) => a + b, 0) / baselineVals.length
    : 0
  return {
    grid: { left: 46, right: 18, top: 26, bottom: 34 },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: eng.map((p) => p.date),
      axisLabel: { fontSize: 10, formatter: (v) => v.slice(5) },
    },
    yAxis: { type: 'value', name: chartSeries.value.axis, nameTextStyle: { fontSize: 10 } },
    series: [
      {
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: eng.map((p) => p.value),
        lineStyle: { width: 2, color: '#36910d' },
        areaStyle: { color: 'rgba(54,145,13,0.10)' },
        markArea: {
          silent: true,
          itemStyle: { color: 'rgba(54,145,13,0.13)' },
          label: { show: true, position: 'insideTop', fontSize: 10, color: '#2a6041', formatter: t('promos.verdict.chartPromo') },
          data: [[{ xAxis: win.start }, { xAxis: win.end }]],
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { type: 'dashed', color: '#8a8f96' },
          label: { formatter: t('promos.verdict.chartBaseline'), fontSize: 10, position: 'insideEndTop' },
          data: [{ yAxis: Math.round(baseline * 10) / 10 }],
        },
      },
    ],
    _base: base,
  }
})

const outcome = computed(() => outcomeSegments(m.value))
/** A euro only exists once somebody has given us the sales figures. */
const hasMoney = computed(() => m.value.margin?.confidence
  && m.value.margin.confidence !== 'na')

/** Which lifecycle tab this promo sits in on the overview — see the back button. */
const backStage = computed(() => {
  if (m.value.phase !== 'complete') return 'running'
  return hasMoney.value ? 'measured' : 'waiting'
})

const showSales = ref(false)
// collapsed: the working answers "prove it", which is the second question, not the
// first. The toggle carries the number it explains so it is obvious what is inside.
const showWork = ref(false)

/**
 * Pre-filled with whatever is already on the promo, so "edit" shows you the numbers
 * you gave rather than three empty boxes you have to remember and retype.
 */
const draftFrom = (p) => ({
  before: { ...(p?.Data?.salesPeriods?.before || {}) },
  during: { ...(p?.Data?.salesPeriods?.during || {}) },
  after: { ...(p?.Data?.salesPeriods?.after || {}) },
})
// salesAsk reads saleDraft, so the draft has to exist first

const saleDraft = ref(draftFrom(promo.value))
/** Edited beside the figures; a flat sum, so it is a scalar and not a period row. */
const setupFrom = (p) => (p?.Data?.fixedCostEur ?? null)
const setupDraft = ref(setupFrom(promo.value))
/** Store-wide only: one average gross margin %, typed once for the whole promo. */
const marginFrom = (p) => (p?.Data?.storeMarginPct ?? null)
const marginDraft = ref(marginFrom(promo.value))
const savedNote = ref(false)



/**
 * Has anything actually been typed? Drives the Save button, because a Save that stays
 * grey after you have changed a figure reads as broken — the demo cannot persist, but
 * the ENABLED state is real behaviour and production inherits it.
 *
 * Compared loosely (`!=`) on purpose: an input gives back "5600" where the stored
 * figure is 5600, and a strict compare would call that a change on every keystroke.
 */
const saleDirty = computed(() => {
  const was = draftFrom(promo.value)
  const setupWas = setupFrom(promo.value)
  // eslint-disable-next-line eqeqeq
  if ((setupDraft.value ?? '') != (setupWas ?? '')) return true
  // eslint-disable-next-line eqeqeq
  if ((marginDraft.value ?? '') != (marginFrom(promo.value) ?? '')) return true
  return ['before', 'during', 'after'].some((k) =>
    ['units', 'revenue', 'cost'].some((f) => {
      const a = saleDraft.value[k]?.[f]
      const b = was[k]?.[f]
      if ((a === '' || a === null || a === undefined)
          && (b === '' || b === null || b === undefined)) return false
      // eslint-disable-next-line eqeqeq
      return a != b
    }))
})

function cancelSales() {
  saleDraft.value = draftFrom(promo.value)
  setupDraft.value = setupFrom(promo.value)
  marginDraft.value = marginFrom(promo.value)
  savedNote.value = false
  showSales.value = false
}
function onSave() {
  // the demo has nowhere to write to and says so; the portal hands it up to be PATCHed
  savedNote.value = true
  if (props.demo) return
  emit('save-sales', {
    salesPeriods: JSON.parse(JSON.stringify(saleDraft.value)),
    fixedCostEur: Number(setupDraft.value) || 0,
    ...(storeWide.value ? { storeMarginPct: Number(marginDraft.value) || null } : {}),
  })
}
// Keyed on the promo's IDENTITY, not the object. The route reuses this component
// between promos, so the figures on screen have to follow the one you are looking at
// — but on the live page a save re-reads the same promo, and watching the object
// reset everything the save had just put on screen, including its confirmation.
watch(() => promo.value?.PromoId, () => {
  const p = promo.value
  saleDraft.value = draftFrom(p)
  setupDraft.value = setupFrom(p)
  marginDraft.value = marginFrom(p)
  showSales.value = false
  showWork.value = false
  savedNote.value = false
})

/**
 * The three stretches we need numbers for, with their exact dates — the same days
 * the calculation uses, so the ask and the maths can never drift apart. For a short
 * promo these are scattered same-weekday blocks, so we list the days rather than a
 * range nobody could total.
 */
// Real today, not the demo's week-shifted one: "has that stretch finished" is a
// question about the actual calendar. See the note in StoralyticDemoPromos.vue.
const salesAsk = computed(() =>
  salesRows(promo.value?.StartDate, promo.value?.EndDate) || [])
const salesNote = computed(() =>
  salesRowsNote(promo.value?.StartDate, promo.value?.EndDate))
/** Thousands separator, or an em dash when there is nothing to show. */
const num = (v) => (v === null || v === undefined || v === ''
  ? '—' : (isNl()
    ? formatNumber(v, { decimals: Number.isInteger(Number(v)) ? 0 : 2 })
    : Number(v).toLocaleString('en-GB')))
/** A euro amount with its sign in front: €1,234 / € 1.234. */
const euro = (v) => (!isNl() ? `€${num(v)}`
  : (v === null || v === undefined || v === '' ? '—'
    : formatEuro(v, { decimals: Number.isInteger(Number(v)) ? 0 : 2 })))
/** A rate already written with two decimals ("87.25"): €87.25 / € 87,25. */
const euroFixed = (s) => (isNl() ? formatEuro(Number(s), { decimals: 2 }) : `€${s}`)
/** A figure exactly as the engine sent it, with the reader's decimal mark. */
const rawNum = (v) => (isNl() && v !== null && v !== undefined ? String(v).replace('.', ',') : v)
/** A per-day figure in the tables: as sent in English, Dutch decimal mark in Dutch. */
const fig = (v) => (isNl() && Number.isInteger(v) ? formatNumber(v) : rawNum(v))
/** "1 day", not "1 days" — a one-day promo (a customer day) is a real case. */
const dayS = (n) => t('promos.common.days', { n: num(n) }, Number(n))
/** Signed euro for a ledger column: a minus where there is one, no plus where not. */
const led = (v) => `${v < 0 ? '−' : ''}${isNl()
  ? formatEuro(Math.abs(Math.round(v))) : `€${Math.abs(Math.round(v)).toLocaleString('en-GB')}`}`

/**
 * What the store itself did over the promo's days — its own door count, not an
 * inference from a neighbouring zone. "Unusual" is measured against the store's own
 * week-to-week wobble, the same band used everywhere else on this page.
 */
const store = computed(() => {
  const st = m.value.storeTraffic
  if (!st || st.deltaPct === null || st.deltaPct === undefined) return null
  return {
    ...st,
    pct: Math.abs(Math.round(st.deltaPct)),
    word: t(st.deltaPct > 0 ? 'promos.verdict.busier' : 'promos.verdict.quieter'),
  }
})

/**
 * THE AUDIT TRAIL as one table: the figures that were entered, and the subtraction
 * that turns them into the euro at the top of the page, in the same rows.
 *
 * This replaces a read-only figures table plus a separate written-out ledger, which
 * between them printed every gross figure twice and the headline euro three times.
 *
 * `normal` is DERIVED FROM THE ENGINE'S EURO rather than recomputed here
 * (`normal = gross − difference − setup`). Two reasons. It cannot drift from the
 * figure in the band, because it is built backwards from it. And it sidesteps a real
 * parity trap: Python rounds a .5 to even and JavaScript rounds it up, so a column
 * recomputed in the browser could print a total a euro off the one the engine sent.
 */
const figures = computed(() => {
  const sp = promo.value?.Data?.salesPeriods || {}
  const mg = m.value.margin || {}
  if (mg.confidence === 'na') return null

  const w = m.value.windows || {}
  const days = { before: w.baseline?.days, during: w.window?.days, after: w.post?.days }
  const asked = salesAsk.value.filter((r) => (sp[r.key] || {}).units != null)
  if (!asked.length) return null

  const setup = mg.fixedCostEur || 0
  // Two routes to a period's gross, and the audit table has to show whichever one
  // the euro was actually built from: the cost the store read off a report, or — on
  // a store promo, where that cost does not exist as a figure — the one average
  // margin % typed once. The column header says which, so the reader is never shown
  // a modelled number dressed as a measured one.
  const pct = Number(promo.value?.Data?.storeMarginPct) || null
  const grossFrom = (v) => {
    if (v.revenue == null) return null
    if (v.cost != null) return v.revenue - v.cost
    return pct ? Math.round(v.revenue * (pct / 100)) : null
  }
  const rows = asked.map((r) => {
    const v = sp[r.key]
    const gross = grossFrom(v)
    const diff = r.key === 'during' ? mg.duringEur
      : (r.key === 'after' && mg.afterKnown ? mg.afterEur : null)
    // the before period IS the normal, so it has nothing to be compared against
    const normal = (r.key === 'before' || gross === null || diff === null)
      ? null : gross - diff - (r.key === 'during' ? setup : 0)
    return {
      key: r.key,
      label: r.label,
      when: days[r.key] ? `${r.when} · ${dayS(days[r.key])}` : r.when,
      units: v.units, revenue: v.revenue, cost: v.cost,
      gross, normal, diff,
      // the setup cost is inside the during figure, as it is in the band above, so
      // say where it went rather than giving it a row of its own
      // a minus and the negative colour: it is money out, sitting inside a figure
      // that may well be positive
      diffNote: (r.key === 'during' && setup)
        ? t('promos.verdict.inclSetup', { eur: euro(setup) }) : null,
    }
  })

  const before = rows.find((r) => r.key === 'before')
  if (!before || before.gross == null || !days.before) return null
  const during = rows.find((r) => r.key === 'during')
  return {
    rows,
    beforeGross: before.gross,
    beforeDays: days.before,
    windowDays: days.during,
    // TWO DECIMALS, not a round euro. The strip shows `days × rate = normal`, and a
    // reader who multiplies the two numbers on screen has to land on the third:
    // 21 × €87 is €1,827, but the column says €1,832. 21 × €87.25 is €1,832.
    perDay: (before.gross / days.before).toFixed(2),
    windowNormal: during?.normal,
  }
})

const anyCostMissing = computed(() =>
  (figures.value?.rows || []).some((r) => r.revenue != null && r.cost == null))
/** Revenue minus what the goods cost. Shown, not typed — it is the subtraction the
 *  euro on this page is built from, so it belongs on screen. */
const grossOf = (row) => {
  const r = saleDraft.value[row.key] || {}
  if (r.revenue == null || r.revenue === '') return null
  // Store-wide the margin comes from one average percentage rather than a cost
  // column, so the gross shown here is worked out from it — the same arithmetic the
  // engine does, so the reader sees the figure the result will be built on.
  if (storeWide.value) {
    const pct = Number(marginDraft.value)
    if (!pct || pct <= 0 || pct >= 100) return null
    return Math.round(r.revenue * (pct / 100))
  }
  if (r.cost == null || r.cost === '') return null
  return r.revenue - r.cost
}
const decayBasis = computed(() => m.value.decay?.basis || 'sales')
const after = computed(() => afterOf(m.value.decay?.after, promo.value?.Data?.mechanic,
                                     decayBasis.value))
const afterCls = computed(() => afterClass(m.value.decay?.after, decayBasis.value))
const afterEurClass = computed(() => {
  if (!m.value.margin?.afterKnown) return 'muted'
  return m.value.margin.afterEur < 0 ? 'neg' : 'pos'
})
/**
 * This zone is a counting line, not an area: it measures how many people came and
 * what sold, never how many stopped to look. Read from the engine's capability block
 * rather than inferred from a zero — a polygon zone where nobody happened to stop is
 * not the same thing, and only a line may have the row withheld.
 */
/**
 * What this promo was measured against, and what that comparison can resolve. On a
 * short promo it is the most useful thing on the page: it is why the answer may be
 * "we cannot tell", and it is the one caveat we can always offer, because both
 * numbers come straight off the windows the engine used. Every figure here is
 * derived — the bar is the data's own day-to-day swing, never a chosen threshold.
 */
const comparison = computed(() => {
  const w = m.value.windows?.window
  const b = m.value.windows?.baseline
  if (!w?.days || !b?.days) return null
  const ran = t('promos.common.days', { n: w.days }, w.days)
  const against = b.mode === 'matched-weekday'
    ? t('promos.verdict.againstMatched', { n: b.days }, b.days)
    : t('promos.verdict.againstDays', { n: b.days })
  const subject = storeWide.value ? t('promos.verdict.theStore') : m.value.zone
  const bar = noiseBar.value
  return t('promos.verdict.comparison', { ran, against })
    + (bar
      ? t('promos.verdict.comparisonBar', { subject, bar: Math.round(bar) })
      : '')
})

/** The meta line under the title: only the parts this promo actually has. */
const subParts = computed(() => [
  storeWide.value ? t('promos.common.wholeStore') : promo.value?.Data?.zoneName,
  promo.value?.Data?.productScope,
  fmtRange(promo.value?.StartDate, promo.value?.EndDate),
].filter(Boolean))
/** Named without the measurement, because the unmeasurable state has none. */
const scopeName = computed(() => (promo.value?.Data?.scope === 'store'
  ? t('promos.verdict.theWholeStore') : (promo.value?.Data?.zoneName || t('promos.verdict.thisZone'))))
const noHistory = computed(() => /no daily/i.test(String(m.value.error || '')))
const countsOnly = computed(() => m.value.capability?.engagementMeasurable === false)
/** The store itself was promoted: no zone, and a different three-term identity. */
const storeWide = computed(() => m.value.capability?.scope === 'store')

const attention = computed(() => attentionFrom(m.value, promo.value?.Data?.mechanic))
const breakEven = computed(() => breakEvenLine(m.value.margin))
const afterWeeks = computed(() => m.value.windows?.post?.weeks
  || Math.round((m.value.windows?.post?.target || 28) / 7))
const pl = computed(() => m.value.promotedSales || {})
/** People who came into the zone per day = store footfall x this zone's reach. */
const visitorsBefore = computed(() => {
  const t = (k) => m.value.decomposition?.terms?.find((x) => x.term === k)
  const f = t('footfall'); const r = t('reach')
  return f && r ? Math.round(f.before * r.before) : null
})
const visitorsAfter = computed(() => {
  const t = (k) => m.value.decomposition?.terms?.find((x) => x.term === k)
  const f = t('footfall'); const r = t('reach')
  return f && r ? Math.round(f.after * r.after) : null
})
/**
 * The rows of the table, across all three periods.
 *
 * The weeks after get their own column because the money and the verdict both lean
 * on them — a table that stopped at the promo window could not support a headline
 * that did not.
 */
/** A change that rounds to zero is "no change", not "+0" — which reads as a bug. */
function deltaText(step, v) {
  if (v == null) return '—'
  if (v === 0) return t('promos.verdict.noChange')
  // points are "pt" / "pts" in English and "%-punt" in Dutch (glossary §2)
  const unit = step.pct ? (isNl() ? ' %-punt' : (Math.abs(v) === 1 ? ' pt' : ' pts')) : '%'
  return `${v > 0 ? '+' : ''}${v}${unit}`
}
function deltaTone(v, step) {
  if (v == null || v === 0) return 't-flat'
  // inside the zone's own normal swing: shown, but not coloured as a result, because
  // the verdict below does not count it as one
  if (step?.bar != null && !step.pct && Math.abs(v) < step.bar) return 't-noise'
  return v > 0 ? 't-good' : 't-bad'
}
/** The average margin a store promo's euro was modelled from, if that is how. */
const marginPctUsed = computed(() => (m.value.margin?.basis === 'store_margin_pct'
  ? (m.value.margin?.marginPct ?? promo.value?.Data?.storeMarginPct ?? null) : null))
/** When the after-window closes, written the way the rest of the page writes dates. */
const afterDue = computed(() => {
  const on = m.value.windows?.post?.readyOn
  return on ? fmtRange(on, on) : null
})
/** The engine's term keys are snake_case; the reader is not. */
const termLabel = (term) => {
  const key = `promos.verdict.terms.${term}`
  return isNl() && te(key) ? t(key) : String(term).replace(/_/g, ' ')
}
/** True when this row's move is too small for us to call it a change. */
function isNoise(step, v) {
  return v != null && v !== 0 && step?.bar != null && !step.pct
    && Math.abs(v) < step.bar
}
/** The bar the camera rows are judged against, for the footnote. */
const noiseBar = computed(() => m.value.detectability?.camera?.mdePct ?? null)
const anyNoise = computed(() => steps.value.some(
  (st) => isNoise(st, st.dDuring) || isNoise(st, st.dAfter)))

const perDay = computed(() => m.value.perDay || {})
const hasAfter = computed(() => !!perDay.value.after)

const steps = computed(() => {
  const P = perDay.value
  if (!P.before || !P.during) return []
  // The plain phrase leads and the platform's own word follows it, so the page
  // teaches the vocabulary the Zones tab and the gauges already use rather than
  // inventing a second one. The units row has no lever — it is a count, not a rate.
  // `bar` is the move this row would need before we would call it a change at all —
  // the camera's own detectable band for what the camera counts, the sales one for
  // what the till counts. Without it the table showed +8% in green while the line
  // underneath said "no more people came", and both were right.
  const camBar = m.value.detectability?.camera?.mdePct ?? null
  const salesBar = m.value.detectability?.sales?.mdePct ?? camBar
  // A counting line sees everyone who crossed it and never whether any of them
  // stopped, so that row is ABSENT rather than zero — and the rate below it is then
  // against everyone who came in, which is a different and weaker thing, so it says
  // which. The engine decides this from the zone's geometry (`capability`); the page
  // never infers it from an engagement figure of zero.
  // A store-wide promo has no reach or engagement to speak of — the store IS the
  // scope — so revenue falls to the classic retail identity instead:
  //     footfall  x  purchase rate  x  average basket
  // "Purchase rate", never "conversion": conversion here means buyers over ENGAGED,
  // and reusing the word store-wide would quietly redefine a KPI across the product.
  if (storeWide.value) {
    return [
      { key: 'foot', label: t('promos.verdict.stepFootStore'), term: 'footfall',
        field: 'visitors', unit: t('promos.verdict.aDay'), bar: camBar },
      { key: 'buy', label: t('promos.verdict.stepTransactions'), field: 'promoUnits',
        unit: t('promos.verdict.aDay'), sub: t('promos.verdict.stepFromFigures'), bar: salesBar },
      { key: 'rate', label: t('promos.verdict.stepRateStore'),
        term: 'purchase_rate', field: 'takeRate', unit: '%', pct: true },
    ].filter((r) => P.before[r.field] != null && P.during[r.field] != null)
      .map(decorate)
  }

  const rows = [
    { key: 'in', label: t('promos.verdict.stepInZone'), term: 'reach',
      field: 'visitors', unit: t('promos.verdict.aDay'), bar: camBar },
    ...(countsOnly.value ? [] : [{
      key: 'stop', label: t('promos.verdict.stepStopped'), term: 'engagement',
      field: 'engaged', unit: t('promos.verdict.aDay'), bar: camBar,
    }]),
    { key: 'buy', label: t('promos.verdict.stepPromoSold'), field: 'promoUnits',
      unit: t('promos.verdict.aDay'), sub: t('promos.verdict.stepFromSales'), bar: salesBar },
    // a percentage change ON a percentage means nothing to a reader, so show points
    { key: 'rate',
      label: countsOnly.value
        ? t('promos.verdict.stepRateLine')
        : t('promos.verdict.stepRateZone'),
      term: 'conversion', field: 'takeRate', unit: '%', pct: true },
  ]
  return rows
    .filter((r) => P.before[r.field] != null && P.during[r.field] != null)
    .map(decorate)

  /** Shared by both identities: the three values and the change between them. */
  function decorate(r) {
      const val = (p) => (p == null || p[r.field] == null ? null
        : (r.pct ? Math.round(p[r.field] * 100) : p[r.field]))
      const b = val(P.before)
      const d = val(P.during)
      const a2 = val(P.after)
      const chg = (v) => (v == null ? null
        : (r.pct ? Math.round(v - b) : Math.round((v - b) / b * 100)))
      const unitFor = (v) => (r.pct
        ? (isNl() ? ' %-punt' : (Math.abs(v) === 1 ? ' pt' : ' pts')) : '%')
      return {
        ...r,
        before: b, during: d, after: a2,
        dDuring: chg(d), dAfter: chg(a2),
        deltaUnit: unitFor(chg(d)),
      }
  }
})

const engagedBefore = computed(() => {
  const w = m.value.windows?.window
  const pts = (m.value.daily?.engaged || []).filter((p) => p.date < w.start)
  return pts.length ? Math.round(pts.reduce((a, p) => a + p.value, 0) / pts.length) : null
})
const engagedAfter = computed(() => {
  const w = m.value.windows?.window
  const pts = (m.value.daily?.engaged || []).filter((p) => p.date >= w.start && p.date <= w.end)
  return pts.length ? Math.round(pts.reduce((a, p) => a + p.value, 0) / pts.length) : null
})
</script>

<style scoped>
.measured-until { margin: -4px 0 10px; color: #4F7EB3; font-size: 0.8rem; }
/* One column width for the whole page. Text fills its block and wraps at the block
   edge — never at a character count, which broke lines mid-phrase well short of the
   container ("...customers paid the" / "...for the 4"). */
/* CONTAINER QUERIES THROUGHOUT — see the note on the overview page. A 300px nav
   sidebar sits beside this, so a tablet in portrait at 820px leaves 500px here, and
   every viewport breakpoint on this page used to miss that case and render a
   seven-column table into half a page. The thresholds below are widths of THIS
   COLUMN. */
.promos-wrap {
  container: page / inline-size;
  margin-left: 20px; padding-right: 20px; padding-bottom: 60px; --block: 880px;
}
.lead, .note, .caveats, .counted, .attn, .result, .attrs, .chart {
  max-width: var(--block);
}
.back {
  margin-top: 18px; background: none; border: none; cursor: pointer;
  font-size: 13px; color: var(--stora-muted, #5b6470); padding: 0;
}
.back:hover { color: var(--stora-accent, #36910d); }
.sub { margin: 0 0 16px; font-size: 13px; color: var(--stora-muted, #5b6470); }
.lead { font-size: 13.5px; line-height: 1.55; color: var(--stora-muted, #5b6470); margin: 0 0 16px; }
.note { font-size: 12.5px; line-height: 1.55; color: var(--stora-muted, #5b6470); margin: 14px 0 0; }

.phase-chip, .entry-chip, .verdict-chip {
  display: inline-block; margin-left: 8px; padding: 2px 9px; border-radius: 999px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
}

/* ---- alarms ---- */
.alarms { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
.alarm {
  display: flex; gap: 12px; padding: 13px 15px; border-radius: 9px;
  background: #fdf3e8; border: 1px solid #f0d6b4;
}
.alarm i { color: #b5762c; font-size: 17px; }
.alarm b { font-size: 13.5px; }
.alarm p { margin: 3px 0 0; font-size: 13px; line-height: 1.5; }

/* ---- verdict row ---- */
.verdict {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 18px; padding: 20px; border-radius: 10px;
  background: var(--stora-bg-alt, #f7f9fa); border: 1px solid var(--stora-border, #e3e6ea);
}
.v-num.neg { color: #a7333f; }
.v-num.pos { color: #2a6041; }

.chart { height: 300px; width: 100%; }

/* ---- caveats ---- */
.caveats { display: flex; flex-direction: column; gap: 14px; }
.cv { display: grid; grid-template-columns: 24px 1fr; gap: 10px; align-items: start; }
.cv i { color: var(--stora-muted, #5b6470); font-size: 15px; margin-top: 2px; }
.cv div { font-size: 13px; line-height: 1.55; }

/* ---- the result card: did -> happened -> made -> do next ----
   One container with hairline bands rather than four stacked boxes, so the eye runs
   straight down it. The two prose lines lead because a reader arriving from a card
   has already seen the money. */
.result {
  max-width: 880px; border-radius: 12px; overflow: hidden;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}
/* the story band: each line named by an eyebrow, set as a deck rather than body copy */
.res-story { padding: 20px 22px 18px; display: flex; flex-direction: column; gap: 16px; }
.res-row { display: grid; grid-template-columns: 124px 1fr; gap: 18px; align-items: baseline; }
.res-eyebrow {
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em;
  color: #9aa7b2; padding-top: 4px;
}
.res-text {
  margin: 0; font-size: 15.5px; line-height: 1.6; font-weight: 400; color: #38454f;
  text-wrap: pretty;
}
.res-text b { font-weight: 700; }

@container page (max-width: 620px) {
  .res-row { grid-template-columns: 1fr; gap: 4px; }
  .res-eyebrow { padding-top: 0; }
}
.res-outcome b { font-weight: 700; }
.t-good { color: #2c7a3f; }
.t-bad { color: #b0464b; }
.t-flat { color: #4b5a66; }
/* inside the zone's normal swing — shown, but not coloured as a result */
.t-noise { color: #9aa7b2; font-weight: 600; }

.res-do {
  display: flex; align-items: flex-start; gap: 4px;
  margin: 0; padding: 14px 22px; font-size: 14px; line-height: 1.55; font-weight: 600;
  border-top: 1px solid var(--stora-border, #e6edf3);
}
.res-do i { font-size: 20px; line-height: 1.1; margin-left: -4px; }
.res-do.v-improved { background: #f3fbf5; color: #2c7a3f; }
.res-do.v-declined { background: #fdf3f3; color: #b0464b; }
.res-do.v-flat { background: #f7fafc; color: #4b5a66; }

/* ---- no figures yet ---- */
.arc-empty { display: block; }
.arc-empty b { font-size: 14.5px; color: #24435f; }
.arc-empty p { margin: 6px 0 12px; font-size: 13.5px; line-height: 1.6; color: #3d4a55; }
.btn-primary {
  padding: 7px 15px; border-radius: 7px; font-size: 13px; font-weight: 600;
  cursor: pointer; background: var(--stora-accent, #4F7EB3); color: #fff; border: none;
}
.btn-primary[disabled] { opacity: .45; cursor: not-allowed; }

.sales-edit { margin: 0; padding: 11px 22px 0; }
.sales-edit button {
  padding: 0; background: none; border: none; cursor: pointer;
  font-size: 12.5px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
}
.sales-edit button:hover { text-decoration: underline; }

/* ---- the three-period ask ---- */
.sales-ask {
  padding: 16px 22px; border-top: 1px solid var(--stora-border, #e6edf3);
  background: #f9fcff;
}
.sales-ask-why {
  margin: 0 0 13px; font-size: 12.5px; line-height: 1.55; color: var(--stora-muted, #6b7a86);
}
.arc-modelled { font-style: italic; }
.setup-cost { display: block; margin: 14px 0 2px; }
.setup-label { display: block; font-size: 12px; font-weight: 700; color: #6b7a87;
  text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 5px; }
.setup-cost input {
  width: 140px; padding: 6px 9px; font-size: 13.5px;
  border: 1px solid var(--stora-border, #e6edf3); border-radius: 7px;
}
.setup-hint { display: block; margin-top: 5px; color: #6b7a87; font-size: 12.5px;
  line-height: 1.5; max-width: 560px; }
.sales-ask-t { width: 100%; border-collapse: collapse; }
.sales-ask-t th {
  text-align: left; padding: 0 10px 7px 0; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .05em; color: #9aa7b2;
}
.sales-ask-t th:not(:first-child) { width: 104px; }
.sales-ask-t td { padding: 0 10px 9px 0; vertical-align: middle; }
.sales-ask-t td b { font-size: 13.5px; color: #38454f; }
.sales-when {
  display: block; margin-top: 2px; font-size: 11.5px; color: var(--stora-muted, #6b7a86);
}
.sales-ask-t input {
  width: 100%; padding: 7px 9px; border-radius: 7px; font-size: 13.5px; color: inherit;
  text-align: right; font-variant-numeric: tabular-nums;
  border: 1px solid var(--stora-border, #e6edf3); background: #fff;
}
.sales-ask-t input:disabled { background: #f6f8fa; cursor: not-allowed; }
.sales-ask-t th small { display: block; font-size: 9px; font-weight: 700;
                        letter-spacing: 0; text-transform: none; color: #b3bfc9; }
.sales-ask-t th.col-gross { color: #38454f; }
.sales-ask-t td.col-gross { text-align: right; padding-right: 2px;
                            font-variant-numeric: tabular-nums; }
.gross-val { font-weight: 800; font-size: 13.5px; color: #2f6f4f; }
.gross-na { color: #c3ccd4; }
.sales-ask-warn {
  display: flex; gap: 8px; margin: 9px 0 0; padding: 9px 11px; border-radius: 8px;
  background: #fff8ec; border: 1px solid #f0dcb8; color: #6a5327;
  font-size: 12px; line-height: 1.6;
}
.sales-ask-warn i { font-size: 13px; line-height: 1.5; }

.sales-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.btn-ghost {
  padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
  font-weight: 600; color: #4b5a66;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}
.btn-ghost:hover { background: var(--stora-bg, #f1f5f8); }
.sales-dirty { font-size: 12px; font-weight: 700; color: #8a6d2f; }
.form-actions {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 16px;
}
.form-hint { font-size: 12px; color: var(--stora-muted, #6b7a86); }
.demo-note, .sales-saved {
  display: flex; gap: 8px; margin: 10px 0 0; padding: 9px 11px; border-radius: 8px;
  background: #eef5fa; border: 1px solid #d6e5f0; color: #35576b;
  font-size: 12.5px; line-height: 1.6;
}
.arc-empty-still { margin-top: 6px; }
.res-be {
  display: flex; gap: 9px; margin: 14px 0 0; padding: 11px 13px; border-radius: 9px;
  background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3);
  font-size: 13.5px; line-height: 1.6; color: #38454f;
}
.res-be i { font-size: 14px; color: var(--stora-muted, #6b7a86); line-height: 1.5; }

/* ---- the audit trail: one toggle, one table ---- */
.audit { margin: 16px 0 0; }
.audit-t {
  display: inline-flex; align-items: center; gap: 7px; padding: 7px 11px 7px 8px;
  border: 1px solid var(--stora-border, #e6edf3); border-radius: 8px;
  background: var(--stora-bg, #f7fafc); cursor: pointer;
  font-size: 12.5px; font-weight: 700; color: #38454f;
}
.audit-t:hover { background: #eef3f7; }
.audit-t i { font-size: 11px; color: var(--stora-muted, #6b7a86); }
.audit-body {
  margin-top: 11px; padding: 13px 15px; border-radius: 10px;
  background: var(--stora-bg, #f7fafc); border: 1px solid var(--stora-border, #e6edf3);
}

/* The derivation, in the same visual language as the money band at the top: big
   figures, operators between them, captions underneath. */
.rate {
  display: grid; grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center; gap: 10px; margin-bottom: 13px;
  padding: 14px 10px; border-radius: 9px; background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
}
.rate-step { text-align: center; min-width: 0; }
.rate-num {
  font-size: 22px; font-weight: 800; line-height: 1.15; color: #38454f;
  font-variant-numeric: tabular-nums;
}
.rate-cap { margin-top: 3px; font-size: 12px; font-weight: 700; color: #4b5a66; }
.rate-sub { margin-top: 1px; font-size: 11px; line-height: 1.4; color: var(--stora-muted, #6b7a86); }
.rate-op { font-size: 17px; font-weight: 700; color: #b3bfc9; }
.rate-answer .rate-num { color: var(--stora-accent, #2f6f8f); }
.rate-then {
  margin: 0 0 14px; font-size: 13px; line-height: 1.65; color: #4b5a66;
}
.rate-then b { color: #2a3942; }
.rate-then i { font-style: normal; font-weight: 600; color: #38454f; }

@container page (max-width: 520px) {
  .rate { grid-template-columns: 1fr; gap: 4px; padding: 13px; }
  .rate-op { text-align: center; }
  .rate-sub { margin-bottom: 4px; }
  .rate-num { font-size: 19px; }
}

.fig { width: 100%; border-collapse: collapse; }
.fig th {
  text-align: right; padding: 0 0 7px 10px; font-size: 9.5px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .04em; color: #9aa7b2; line-height: 1.3;
}
.fig th:first-child { text-align: left; padding-left: 0; }
.fig th:not(:first-child) { width: 78px; }
.fig th.col-diff { width: 104px; color: #38454f; }
.fig td { padding: 5px 0 5px 10px; font-size: 13px; vertical-align: top; }
.fig td:first-child { padding-left: 0; }
.fig tbody tr + tr td { border-top: 1px solid #e9eff4; }
.fig td b { font-size: 13px; color: #38454f; }
.fig-when {
  display: block; margin-top: 2px; font-size: 11px; line-height: 1.45;
  color: var(--stora-muted, #6b7a86);
}
.fig .num {
  text-align: right; font-variant-numeric: tabular-nums; color: #4b5a66;
  white-space: nowrap;
}
.fig .num.strong { font-weight: 700; color: #38454f; }
.fig td.col-diff {
  text-align: right; font-variant-numeric: tabular-nums; font-weight: 800;
  white-space: nowrap;
}
.fig-note { display: block; margin-top: 2px; font-size: 10.5px; font-weight: 600; }
.fig-note.neg { color: #a33a3a; }
.fig-total td {
  padding-top: 9px; border-top: 2px solid #d6e0e8 !important;
  font-weight: 800; font-size: 13.5px; color: #2a3942;
}
.fig-total td:first-child { text-align: right; }
.fig .pos { color: #2f6f4f; }
.fig .neg { color: #a33a3a; }

@container page (max-width: 700px) {
  .fig, .fig tbody, .fig tr, .fig td { display: block; width: 100%; }
  .fig thead { display: none; }
  .fig tbody tr { padding: 9px 0; border-top: 1px solid #e9eff4; }
  .fig tbody tr + tr td { border-top: 0; }
  .fig td { display: flex; justify-content: space-between; gap: 14px; padding: 2px 0; }
  .fig td[data-label]::before {
    content: attr(data-label); font-size: 10px; font-weight: 800;
    text-transform: uppercase; letter-spacing: .04em; color: #9aa7b2;
  }
  .fig td:first-child { display: block; margin-bottom: 4px; }
  .fig-total td { display: flex; }
  .fig-total td[colspan] { display: none; }
}

@container page (max-width: 640px) {
  .sales-ask-t, .sales-ask-t tbody, .sales-ask-t tr, .sales-ask-t td {
    display: block; width: 100%;
  }
  .sales-ask-t thead { display: none; }
  .sales-ask-t tr { padding: 0 0 14px; }
  .sales-ask-t td { padding: 0 0 7px; }
  .sales-ask-t td.col-gross { text-align: left; }
  .sales-ask-t td[data-label]::before {
    content: attr(data-label); display: block; margin-bottom: 3px;
    font-size: 10px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .05em; color: #9aa7b2;
  }
}

/* ---- the money band inside the card ---- */
.arc {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1.15fr;
  align-items: start;
  gap: 16px;
  padding: 18px 22px;
  background: var(--stora-bg, #f7fafc);
  border-top: 1px solid var(--stora-border, #e6edf3);
}
.arc-step { min-width: 0; }
.arc-net { padding-left: 18px; border-left: 2px solid #dfe7ee; }
.arc-eur {
  font-size: 1.55rem; font-weight: 800; line-height: 1.15;
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.arc-net .arc-eur { font-size: 1.95rem; }
.arc-eur.neg { color: #b0464b; }
.arc-eur.pos { color: #2c7a3f; }
.arc-eur.muted { color: #8b98a3; font-size: 1.05rem; font-weight: 700; }
.arc-cap {
  margin-top: 5px; font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .045em; color: var(--stora-muted, #6b7a86); line-height: 1.45;
}
.arc-sub { margin-top: 3px; font-size: 11.5px; color: var(--stora-muted, #6b7a86); line-height: 1.45; }
.arc-op {
  font-size: 1.3rem; font-weight: 700; color: #b6c1cb; line-height: 1.5;
  align-self: start; padding-top: 2px;
}

/* ---------- responsive ----------
   Laptop and desktop share the 880px column. Below it the three money figures stop
   fitting side by side, below 680 the eyebrow column squeezes the prose, and below
   640 a four-column table of long labels cannot be read at all — so it stacks into
   one block per row, each value keeping its heading. */
@container page (max-width: 820px) {
  .arc { grid-template-columns: 1fr; gap: 14px; }
  .arc-op { display: none; }
  .arc-net { padding-left: 0; padding-top: 14px; border-left: none;
             border-top: 2px solid #dfe7ee; }
  .arc-net .arc-eur { font-size: 1.7rem; }
}

/* Once the sidebar overlays instead of offsetting, the app shell owns the gutter —
   so this page drops its own and sits exactly where every other page does. Adding a
   gutter here put the promo pages 14px further from the edge than their neighbours. */
@media (max-width: 768px) {
  .promos-wrap { margin-left: 0; padding-right: 0; }
}

@container page (max-width: 620px) {
  .res-story { padding: 16px 16px 14px; }
  .arc { padding: 16px; }
  .res-do { padding: 13px 16px; }
  .chart { height: 240px; }

  /* the table becomes one block per row; thead is dropped and each value carries
     its own heading, so nothing loses its meaning */
  .counted thead { display: none; }
  .counted, .counted tbody, .counted tr, .counted td { display: block; width: 100%; }
  .counted tr {
    padding: 13px 0; border-bottom: 1px solid var(--stora-border, #e6edf3);
  }
  .counted tbody tr:last-child { border-bottom: none; }
  .counted td {
    padding: 0; text-align: left; border: none; background: none;
    display: flex; justify-content: space-between; align-items: baseline; gap: 14px;
  }
  .counted td:first-child { font-weight: 600; margin-bottom: 7px; display: block; }
  .counted td[data-label]::before {
    content: attr(data-label);
    font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
    color: #9aa7b2;
  }
  .counted td[data-label] { padding: 3px 0; }
  .counted .delta { display: inline; margin: 0 0 0 7px; }
}

/* ---- the before/after table ----
   Right-aligned tabular figures, the "while it ran" column tinted so the eye lands
   on it, and a change column so no row makes the reader do arithmetic. */
.counted { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.counted th {
  text-align: right; font-size: 10px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .06em; color: #9aa7b2;
  padding: 0 14px 9px; border-bottom: 1px solid var(--stora-border, #e6edf3);
}
.counted th:first-child { text-align: left; padding-left: 0; }
.counted th:last-child, .counted td:last-child { padding-right: 0; }
.counted td {
  padding: 13px 14px; text-align: right;
  border-bottom: 1px solid var(--stora-border, #e6edf3);
  font-variant-numeric: tabular-nums; color: var(--stora-muted, #6b7a86);
}
.counted td:first-child {
  text-align: left; padding-left: 0; color: #38454f; line-height: 1.35;
}
.counted tbody tr:last-child td { border-bottom: none; }
.counted .col-after { background: #f5f8fb; }
.counted td.col-after b { color: #22303b; font-weight: 800; }
/* the change sits under its value, so each period carries its own comparison */
.counted .delta {
  display: block; margin-top: 2px; font-weight: 800; font-size: 11.5px;
}
.src {
  display: block; font-size: 11px; font-weight: 400;
  color: var(--stora-muted, #6b7a86); margin-top: 2px;
}
/* the platform's own word for the step, quiet enough to stay secondary to the
   plain phrase it follows */
.term { margin-left: 6px; font-size: 11.5px; color: #9aa7b2; }

/* ---- the conclusion drawn from the table, tied to it rather than floating ---- */
.attn {
  display: grid; grid-template-columns: 22px 1fr; gap: 12px; align-items: start;
  margin-top: 20px; padding: 15px 18px;
  border-radius: 10px; border: 1px solid #d7e6fb;
  border-left: 3px solid var(--stora-accent, #4F7EB3);
  background: #f4f8fd;
}
.attn > i { font-size: 16px; color: var(--stora-accent, #4F7EB3); line-height: 1.4; }
.attn b { display: block; font-size: 14.5px; color: #24435f; line-height: 1.45; }
.attn p { margin: 6px 0 0; font-size: 13.5px; line-height: 1.6; color: #3d4a55; }
.attn-soft { background: #f7fafc; border-color: #e1e8ee; border-left-color: #b6c1cb; }
.attn-soft > i { color: #9aa7b2; }
.attn-soft b { color: #4b5a66; }
.attn-caveat { color: var(--stora-muted, #6b7a86) !important; font-size: 12.5px !important; }

/* ---- the shared result palette ---- */
.verdict-chip {
  font-size: 0.72rem; font-weight: 800; padding: 3px 10px;
  border-radius: 999px; white-space: nowrap; display: inline-block;
}
.v-early { background: #fff7e6; color: #b66a00; }
.v-improved { background: #f3fbf5; color: #2c7a3f; }
.v-declined { background: #fdf3f3; color: #b0464b; }
.v-flat { background: #f2f5f8; color: #6c7a86; }

/* ---- would you run it again ---- */
.why { display: block; margin-top: 16px; max-width: 520px; }
.why > span {
  display: block; margin-bottom: 5px; font-size: 12px; font-weight: 600;
  color: var(--stora-muted, #6b7a86);
}
.why input {
  width: 100%; padding: 8px 10px; border-radius: 7px; font-size: 13.5px; color: inherit;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}

/* ---- attribution ---- */
.attrs { display: flex; gap: 10px; flex-wrap: wrap; }
.attr {
  padding: 8px 14px; border-radius: 999px; cursor: pointer; font-size: 13px;
  border: 1px solid var(--stora-border, #d8dde3);
}
.attr.sel { border-color: var(--stora-accent, #36910d); background: color-mix(in srgb, var(--stora-accent, #36910d) 10%, transparent); }
.attr input { margin-right: 6px; }

</style>
